import React, { useMemo } from 'react';
import { ArrowLeft, Skull, Crown, Coins, Swords, Zap, Shield } from 'lucide-react';

// Compact post-run timeline of every recorded floor event.
export default function CombatHistory({ summary, onBack }) {
  const events = summary?.history || [];
  const totals = useMemo(() => {
    let dmg = 0, gold = 0, cards = 0, bosses = 0, elites = 0;
    for (const e of events) {
      if (e.kind === 'combat') { dmg += e.dmgTaken || 0; if (e.isBoss) bosses++; if (e.isElite) elites++; }
      if (e.kind === 'reward') { gold += e.gold || 0; cards += 1; }
    }
    return { dmg, gold, cards, bosses, elites };
  }, [events]);

  return (
    <div className="w-full max-w-4xl mx-auto brutal-border p-4 sm:p-6 bg-[#0a0a0a]" data-testid="combat-history">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.4em] text-[color:var(--text-dim)]">✦ RUN TIMELINE ✦</div>
        {onBack && (
          <button className="brutal-btn text-xs flex items-center gap-1" onClick={onBack} data-testid="history-back">
            <ArrowLeft size={12}/> BACK
          </button>
        )}
      </div>
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
        <div className="brutal-border-neon p-2 text-center" data-testid="hist-total-dmg">
          <div className="font-mono text-[10px] text-[color:var(--text-dim)]">DMG TAKEN</div>
          <div className="font-display font-black text-lg text-[color:var(--red)]">{totals.dmg}</div>
        </div>
        <div className="brutal-border-neon p-2 text-center" data-testid="hist-total-gold">
          <div className="font-mono text-[10px] text-[color:var(--text-dim)]">GOLD FOUND</div>
          <div className="font-display font-black text-lg text-[color:var(--pink)]">{totals.gold}</div>
        </div>
        <div className="brutal-border-neon p-2 text-center" data-testid="hist-total-cards">
          <div className="font-mono text-[10px] text-[color:var(--text-dim)]">CARDS TAKEN</div>
          <div className="font-display font-black text-lg text-[color:var(--neon)]">{totals.cards}</div>
        </div>
        <div className="brutal-border-neon p-2 text-center" data-testid="hist-total-elites">
          <div className="font-mono text-[10px] text-[color:var(--text-dim)]">ELITES KILLED</div>
          <div className="font-display font-black text-lg">{totals.elites}</div>
        </div>
        <div className="brutal-border-neon p-2 text-center" data-testid="hist-total-bosses">
          <div className="font-mono text-[10px] text-[color:var(--text-dim)]">BOSSES</div>
          <div className="font-display font-black text-lg">{totals.bosses}</div>
        </div>
      </div>

      <div className="mt-5 space-y-1 max-h-[50vh] overflow-y-auto pr-1">
        {events.length === 0 && (
          <div className="text-center text-xs text-[color:var(--text-dim)] py-6" data-testid="history-empty">No events recorded.</div>
        )}
        {events.map((e, i) => (
          <HistoryRow key={i} event={e} index={i} />
        ))}
      </div>
    </div>
  );
}

function HistoryRow({ event: e, index }) {
  if (e.kind === 'combat') {
    const Icon = e.isBoss ? Crown : (e.isElite ? Skull : Swords);
    const color = e.isBoss ? 'var(--pink)' : e.isElite ? 'var(--red)' : 'var(--neon)';
    return (
      <div className="flex items-center gap-3 border-2 border-[#222] px-3 py-2 text-xs" data-testid={`history-row-${index}`}>
        <div className="font-mono text-[10px] text-[color:var(--text-dim)] w-10">F{e.floor}</div>
        <Icon size={14} style={{ color }}/>
        <div className="flex-1 font-mono uppercase" style={{ color: e.victory ? '#fff' : 'var(--red)' }}>
          {e.victory ? 'KILLED' : 'DIED TO'} {e.enemyName}
          {e.affix && <span className="ml-1 text-[10px]" style={{ color: 'var(--red)' }}>[{e.affix.toUpperCase()}]</span>}
        </div>
        <div className="text-[10px] text-[color:var(--text-dim)] font-mono">
          T{e.turns} · {e.dmgTaken} dmg · big {e.biggestHit}
          {e.synergies > 0 && <span className="ml-2 text-[color:var(--pink)]">{e.synergies} SYNC</span>}
        </div>
      </div>
    );
  }
  if (e.kind === 'reward') {
    return (
      <div className="flex items-center gap-3 border-2 border-[#222] px-3 py-2 text-xs" data-testid={`history-row-${index}`}>
        <div className="font-mono text-[10px] text-[color:var(--text-dim)] w-10">F{e.floor}</div>
        <Coins size={14} style={{ color: 'var(--pink)' }}/>
        <div className="flex-1 font-mono uppercase">
          TOOK <span className="text-[color:var(--neon)]">{e.cardId}</span> · +{e.gold} gold
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 border-2 border-[#222] px-3 py-2 text-xs opacity-60" data-testid={`history-row-${index}`}>
      <div className="font-mono text-[10px] text-[color:var(--text-dim)] w-10">F{e.floor || '?'}</div>
      <Zap size={14}/>
      <div className="flex-1 font-mono">{e.kind}</div>
    </div>
  );
}
